import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Zap, Info } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtNum } from "@/lib/format";

const emptyRule = { action: "", points: 100, unit: "", description: "", active: true, manual_approval: false, annual_cap: 0 };

export default function AdminRules() {
  usePageTitle("Reglas manuales · Puntos");
  const canGrant = (useAuth().user?.permissions || []).includes("users");
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(emptyRule);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<any>(null);

  const load = () => {
    setFailed(false);
    return api.get("/admin/rules").then((r) => setRules(r.data)).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const open = (r?: any) => { setEditing(r ? r.id : "new"); setForm(r ? { ...r } : emptyRule); setErrors({}); };

  const save = async () => {
    const e: Record<string, string> = {};
    if (!form.action?.trim()) e.action = "Escribe qué hace el cliente para ganar los puntos.";
    const pts = Number(form.points);
    if (!Number.isInteger(pts) || pts < 1) e.points = "Los puntos deben ser un número entero mayor que 0.";
    const cap = Number(form.annual_cap || 0);
    if (!Number.isInteger(cap) || cap < 0) e.annual_cap = "El tope debe ser un número entero, 0 o más.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const payload = { ...form, action: form.action.trim(), points: pts, annual_cap: cap };
      if (editing === "new") await api.post("/admin/rules", payload);
      else await api.put(`/admin/rules/${editing}`, payload);
      toast.success(payload.active ? "Regla guardada. Ya se puede elegir al entregar puntos." : "Regla guardada como inactiva: no se muestra ni se puede usar.");
      setEditing(null); load();
    } catch (err: any) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/rules/${toDelete.id}`); toast.success(`Regla “${toDelete.action}” eliminada`); load(); }
    catch (err: any) { toast.error(apiError(err)); throw err; }
  };

  const err = (k: string) => errors[k] && <p id={`rule-${k}-error`} role="alert" className="text-xs text-red-700 mt-1">{errors[k]}</p>;

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-rules-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-slate-600 max-w-2xl">
          Al {canGrant ? <Link to="/admin/puntos" className="text-navy underline">entregar a un cliente</Link> : "entregar puntos a un cliente"} se elige una de estas reglas y se aplica su máximo anual.
          Los puntos que se entregan solos (cumpleaños, registro, plan pagado) se configuran en <Link to="/admin/puntos/automaticas" className="text-navy underline">Reglas automáticas</Link>.
        </p>
        <Button data-testid="new-rule-button" onClick={() => open()} className="gold-gradient-bg text-white font-semibold rounded-full">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nueva regla
        </Button>
      </div>

      {failed ? <LoadError what="las reglas" onRetry={load} /> : rules.length === 0 ? (
        <EmptyState icon={Zap}>Aún no hay reglas. Crea la primera con “Nueva regla”.</EmptyState>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rules.map((r) => (
            <div key={r.id} data-testid="admin-rule-card" className={`card-obsidian p-5 ${r.active ? "" : "opacity-75"}`}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <h2 className="font-semibold text-slate-900">{r.action}</h2>
                <span className="text-gold-ink font-mono font-semibold">+{fmtNum(r.points)}</span>
              </div>
              {r.unit && <p className="text-xs text-slate-600 mb-2">{r.unit}</p>}
              <p className="text-sm text-slate-600 mb-4">{r.description}</p>
              <ul className="flex flex-wrap gap-1.5 mb-4 text-xs">
                {!r.active && <li className="bg-slate-200 text-slate-700 rounded-full px-2 py-0.5 font-medium">Inactiva</li>}
                {r.annual_cap > 0 && <li className="bg-gold/15 text-gold-ink rounded-full px-2 py-0.5 font-medium">Máximo {fmtNum(r.annual_cap)} pts al año por cliente</li>}
                {r.manual_approval && <li className="bg-navy/10 text-navy rounded-full px-2 py-0.5 font-medium">Validar antes de entregar</li>}
              </ul>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => open(r)} className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-100 h-9">
                  <Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Editar
                </Button>
                <Button size="sm" variant="outline" onClick={() => setToDelete(r)} aria-label={`Eliminar regla ${r.action}`} className="border-red-500/30 text-red-700 hover:bg-red-500/10 h-9">
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o: boolean) => !o && setEditing(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing === "new" ? "Nueva regla" : "Editar regla"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="rule-action" className="text-slate-700 text-sm">¿Qué hace el cliente? *</Label>
              <Input id="rule-action" data-testid="rule-action-input" value={form.action} onChange={(e: any) => setForm({ ...form, action: e.target.value })} placeholder="Ej: Referir a un cliente" aria-invalid={!!errors.action} aria-describedby={errors.action ? "rule-action-error" : undefined} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              {err("action")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="rule-points" className="text-slate-700 text-sm">Puntos que gana *</Label>
                <Input id="rule-points" data-testid="rule-points-input" type="number" min={1} value={form.points} onChange={(e: any) => setForm({ ...form, points: e.target.value })} aria-invalid={!!errors.points} aria-describedby={errors.points ? "rule-points-error" : undefined} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
                {err("points")}
              </div>
              <div>
                <Label htmlFor="rule-unit" className="text-slate-700 text-sm">Cada cuánto (texto para el cliente)</Label>
                <Input id="rule-unit" value={form.unit} onChange={(e: any) => setForm({ ...form, unit: e.target.value })} placeholder="Ej: por referido válido" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              </div>
            </div>
            <div>
              <Label htmlFor="rule-description" className="text-slate-700 text-sm">Descripción (la ven los clientes)</Label>
              <Textarea id="rule-description" value={form.description} onChange={(e: any) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
            </div>
            <div>
              <Label htmlFor="rule-cap" className="text-slate-700 text-sm">Máximo de puntos al año por cliente (0 = sin límite)</Label>
              <Input id="rule-cap" data-testid="rule-annualcap-input" type="number" min={0} value={form.annual_cap} onChange={(e: any) => setForm({ ...form, annual_cap: e.target.value })} aria-invalid={!!errors.annual_cap} aria-describedby="rule-cap-help" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900" />
              <p id="rule-cap-help" className="text-xs text-slate-600 mt-1">Se aplica al entregar puntos eligiendo esta regla: si se pasa del máximo, solo se entrega lo que falta. La carga por Excel no lo aplica.</p>
              {err("annual_cap")}
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="rule-manual-approval-checkbox" type="checkbox" checked={!!form.manual_approval} onChange={(e) => setForm({ ...form, manual_approval: e.target.checked })} className="w-4 h-4 mt-0.5 accent-navy" />
              <span className="text-sm text-slate-700">
                Validar el caso antes de entregar los puntos
                <span className="flex items-start gap-1 text-xs text-slate-600 mt-0.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" /> Es una indicación para el equipo: se muestra al entregar, pero el sistema no bloquea la entrega.</span>
              </span>
            </label>
            <div className="flex items-center gap-2">
              <Switch id="rule-active" checked={!!form.active} onCheckedChange={(v: boolean) => setForm({ ...form, active: v })} />
              <Label htmlFor="rule-active" className="text-sm text-slate-700">Activa (visible para clientes y disponible al entregar puntos)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button data-testid="save-rule-button" onClick={save} disabled={saving} className="gold-gradient-bg text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        danger
        title={`¿Eliminar la regla “${toDelete?.action || ""}”?`}
        description={<>
          <p>Desaparecerá del portal y ya no se podrá elegir al entregar puntos. Los puntos ya entregados no cambian.</p>
          <p>Si solo quieres pausarla, edítala y desactívala.</p>
        </>}
        confirmLabel="Eliminar regla"
        onConfirm={remove}
      />
    </div>
  );
}
